// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Settings → AI Models (**ADR-0080**).
 *
 * **The API key lives in this component's state and nowhere else.** It is held
 * only while the operator is typing, sent once, and cleared as soon as the
 * request settles — success or failure. It is never put in a query cache, never
 * written to storage, and never read back: the provider view has no field that
 * could carry it, so what the screen renders afterwards is a state, a version
 * and a last four.
 *
 * Controls are hidden by capability for usability only. The BFF and Platform
 * each authorize independently, so a hidden control is a convenience and never
 * the boundary.
 */

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState, type ReactNode } from 'react';

import {
  capabilities,
  hasCapability,
  providerFields,
  providerLabels,
  providerTypes,
  useApiClients,
  useSession,
  type ProviderType,
} from '@eai/ui-shared';

/**
 * Label a provider type, falling back to the raw value.
 *
 * A provider type the build does not know is still rendered rather than hidden:
 * an operator looking at a row Platform returned should see what it is.
 *
 * @param providerType - The stored provider type.
 * @returns The label to show.
 */
function providerLabelFor(providerType: string): string {
  return providerType in providerLabels
    ? providerLabels[providerType as ProviderType]
    : providerType;
}

/** Warning shown wherever a control spends the University's money. */
export const BILLING_WARNING =
  'Testing a credential sends one minimal request to the provider and may incur a small charge.';

/** Render the AI models settings screen. */
export function AiModelsScreen(): ReactNode {
  const clients = useApiClients();
  const { session } = useSession();

  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  // New-provider form.
  const [providerType, setProviderType] = useState<ProviderType>('anthropic');
  const [providerName, setProviderName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [organization, setOrganization] = useState('');
  const [project, setProject] = useState('');

  // New-model form.
  const [modelProvider, setModelProvider] = useState('');
  const [modelName, setModelName] = useState('');
  const [modelRef, setModelRef] = useState('');

  const canRead = hasCapability(session, capabilities.aiSettingsRead);
  const canWriteProviders = hasCapability(session, capabilities.aiSettingsProvidersWrite);
  const canWriteModels = hasCapability(session, capabilities.aiSettingsModelsWrite);
  const canWriteCredentials = hasCapability(session, capabilities.aiSettingsCredentialsWrite);
  const canWriteRouting = hasCapability(session, capabilities.aiSettingsRoutingWrite);
  const canTest = hasCapability(session, capabilities.aiSettingsProvidersTest);

  // The same loading discipline as every other screen: the query owns fetching,
  // retries and invalidation, so nothing here sets state from an effect.
  const settings = useQuery({
    queryKey: ['ai-settings'],
    enabled: canRead,
    queryFn: async () => {
      const [providerList, modelList, routingView] = await Promise.all([
        clients.aiSettings.listProviders(),
        clients.aiSettings.listModels(),
        clients.aiSettings.readRouting(),
      ]);
      return { providerList, modelList, routingView };
    },
  });

  const providers = settings.data?.providerList ?? [];
  const models = settings.data?.modelList ?? [];
  const routing = settings.data?.routingView ?? null;

  const refresh = useCallback(async (): Promise<void> => {
    await settings.refetch();
  }, [settings]);

  const run = useCallback(
    async (key: string, action: () => Promise<void>, success?: string): Promise<void> => {
      setPending(key);
      setFailure(null);
      setNotice(null);
      try {
        await action();
        await refresh();
        if (success !== undefined) {
          setNotice(success);
        }
      } catch (error: unknown) {
        setFailure(error instanceof Error ? error.message : 'The request was refused.');
      } finally {
        setPending(null);
      }
    },
    [refresh],
  );

  if (!canRead) {
    return (
      <Alert severity="info" role="status">
        You do not have access to AI settings.
      </Alert>
    );
  }

  const shows = (field: string): boolean => providerFields[providerType].includes(field);

  const addProvider = async (): Promise<void> => {
    const config: Record<string, string> = {};
    if (shows('organization') && organization !== '') {
      config['project_id'] = organization;
    }
    if (shows('project') && project !== '') {
      config['deployment'] = project;
    }
    const created = await clients.aiSettings.createProvider({
      display_name: providerName,
      provider_type: providerType,
      enabled: true,
      config,
    });
    // The key is sent once, on the provider that was just created, and dropped
    // immediately afterwards whatever happened.
    try {
      if (apiKey !== '') {
        await clients.aiSettings.rotateCredential({
          provider_id: created.provider_id,
          api_key: apiKey,
        });
      }
    } finally {
      setApiKey('');
    }
    setProviderName('');
    setOrganization('');
    setProject('');
  };

  const chain = routing?.entries ?? [];
  const primaryId = chain.find((entry) => entry.position === 0)?.model_id ?? '';
  const fallbackIds = chain
    .filter((entry) => entry.position > 0)
    .sort((left, right) => left.position - right.position)
    .map((entry) => entry.model_id);

  const saveChain = async (primary: string, fallbacks: readonly string[]): Promise<void> => {
    const entries = [
      ...(primary === '' ? [] : [{ position: 0, model_id: primary }]),
      ...fallbacks.map((modelId, index) => ({ position: index + 1, model_id: modelId })),
    ];
    await clients.aiSettings.activateRouting({
      entries,
      expected_version: routing?.version ?? undefined,
    });
  };

  const move = (index: number, delta: number): void => {
    const target = index + delta;
    if (target < 0 || target >= fallbackIds.length) {
      return;
    }
    const moved = fallbackIds[index];
    const displaced = fallbackIds[target];
    if (moved === undefined || displaced === undefined) {
      return;
    }
    const next = fallbackIds.map((modelId, position) => {
      if (position === index) {
        return displaced;
      }
      if (position === target) {
        return moved;
      }
      return modelId;
    });
    void run('routing', () => saveChain(primaryId, next), 'Fallback order saved.');
  };

  const modelLabel = (modelId: string): string => {
    const model = models.find((candidate) => candidate.model_id === modelId);
    if (model === undefined) {
      return modelId;
    }
    const provider = providers.find((item) => item.provider_id === model.provider_id);
    return `${provider?.display_name ?? 'Unknown provider'} / ${model.display_name}`;
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h4" component="h1">
        AI models
      </Typography>

      {failure === null ? null : (
        <Alert severity="error" role="alert" data-testid="ai-settings-error">
          {failure}
        </Alert>
      )}
      {notice === null ? null : (
        <Alert severity="success" role="status" data-testid="ai-settings-notice">
          {notice}
        </Alert>
      )}

      {/* --- providers ---------------------------------------------------- */}
      <Card>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            Providers
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            {BILLING_WARNING}
          </Alert>

          <Table size="small" aria-label="Configured providers">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Credential</TableCell>
                <TableCell>Last validated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {providers.map((provider) => (
                <TableRow key={provider.provider_id}>
                  <TableCell>{provider.display_name}</TableCell>
                  <TableCell>{providerLabelFor(provider.provider_type)}</TableCell>
                  <TableCell>
                    {provider.credential === null || provider.credential === undefined ? (
                      <Chip size="small" label="Missing" />
                    ) : (
                      <Chip
                        size="small"
                        label={`${provider.credential.state} ••••${provider.credential.key_tail}`}
                      />
                    )}
                  </TableCell>
                  <TableCell>{provider.credential?.last_validated_at ?? '—'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {canTest ? (
                        <Button
                          size="small"
                          disabled={pending !== null}
                          onClick={() => {
                            void run(
                              `test:${provider.provider_id}`,
                              async () => {
                                await clients.aiSettings.testProvider(provider.provider_id);
                              },
                              'Connection tested.',
                            );
                          }}
                        >
                          Test connection
                        </Button>
                      ) : null}
                      {canWriteProviders ? (
                        <Button
                          size="small"
                          color="error"
                          disabled={pending !== null}
                          onClick={() => {
                            void run(
                              `remove:${provider.provider_id}`,
                              async () => {
                                await clients.aiSettings.deleteProvider(provider.provider_id);
                              },
                              'Provider removed.',
                            );
                          }}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {canWriteProviders ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" component="h3" gutterBottom>
                Add a provider
              </Typography>
              <Stack spacing={2}>
                <TextField
                  select
                  label="Provider type"
                  value={providerType}
                  onChange={(event) => {
                    setProviderType(event.target.value as ProviderType);
                  }}
                >
                  {providerTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {providerLabels[type]}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Provider name"
                  value={providerName}
                  onChange={(event) => {
                    setProviderName(event.target.value);
                  }}
                />
                {canWriteCredentials ? (
                  <TextField
                    label="API key"
                    type="password"
                    value={apiKey}
                    autoComplete="off"
                    helperText="Sent once and never shown again. Only the last four characters are kept."
                    onChange={(event) => {
                      setApiKey(event.target.value);
                    }}
                  />
                ) : null}
                {shows('organization') ? (
                  <TextField
                    label="Organization"
                    value={organization}
                    onChange={(event) => {
                      setOrganization(event.target.value);
                    }}
                  />
                ) : null}
                {shows('project') ? (
                  <TextField
                    label="Project"
                    value={project}
                    onChange={(event) => {
                      setProject(event.target.value);
                    }}
                  />
                ) : null}
                <Box>
                  <Button
                    variant="contained"
                    disabled={pending !== null || providerName === ''}
                    onClick={() => {
                      void run('add-provider', addProvider, 'Provider added.');
                    }}
                  >
                    Save provider
                  </Button>
                </Box>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                More providers will be added later using the same interface.
              </Typography>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      {/* --- models -------------------------------------------------------- */}
      <Card>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            Models
          </Typography>
          <Table size="small" aria-label="Configured models">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Model identifier</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Validated</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {models.map((model) => {
                const provider = providers.find(
                  (candidate) => candidate.provider_id === model.provider_id,
                );
                const role =
                  model.model_id === primaryId
                    ? 'Primary'
                    : fallbackIds.includes(model.model_id)
                      ? `Fallback ${String(fallbackIds.indexOf(model.model_id) + 1)}`
                      : '—';
                return (
                  <TableRow key={model.model_id}>
                    <TableCell>{model.display_name}</TableCell>
                    <TableCell>{provider?.display_name ?? '—'}</TableCell>
                    <TableCell>{model.provider_model_ref}</TableCell>
                    <TableCell>{model.enabled ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{model.validated ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{role}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {canWriteModels ? (
                          <Button
                            size="small"
                            disabled={pending !== null}
                            onClick={() => {
                              void run(
                                `toggle:${model.model_id}`,
                                async () => {
                                  await clients.aiSettings.amendModel({
                                    model_id: model.model_id,
                                    enabled: !model.enabled,
                                  });
                                },
                                model.enabled ? 'Model disabled.' : 'Model enabled.',
                              );
                            }}
                          >
                            {model.enabled ? 'Disable' : 'Enable'}
                          </Button>
                        ) : null}
                        {canWriteModels ? (
                          <Button
                            size="small"
                            color="error"
                            disabled={pending !== null}
                            onClick={() => {
                              void run(
                                `remove-model:${model.model_id}`,
                                async () => {
                                  await clients.aiSettings.deleteModel(model.model_id);
                                },
                                'Model removed.',
                              );
                            }}
                          >
                            Remove
                          </Button>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {canWriteModels ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" component="h3" gutterBottom>
                Add a model
              </Typography>
              <Stack spacing={2}>
                <TextField
                  select
                  label="Provider"
                  value={modelProvider}
                  onChange={(event) => {
                    setModelProvider(event.target.value);
                  }}
                >
                  {providers.map((provider) => (
                    <MenuItem key={provider.provider_id} value={provider.provider_id}>
                      {provider.display_name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Model name"
                  value={modelName}
                  onChange={(event) => {
                    setModelName(event.target.value);
                  }}
                />
                <TextField
                  label="Provider model identifier"
                  value={modelRef}
                  helperText="Enter the identifier the provider publishes. Catalogues change, so this is not a fixed list."
                  onChange={(event) => {
                    setModelRef(event.target.value);
                  }}
                />
                <Box>
                  <Button
                    variant="contained"
                    disabled={pending !== null || modelProvider === '' || modelRef === ''}
                    onClick={() => {
                      void run(
                        'add-model',
                        async () => {
                          await clients.aiSettings.createModel({
                            provider_id: modelProvider,
                            display_name: modelName,
                            provider_model_ref: modelRef,
                            enabled: true,
                          });
                          setModelName('');
                          setModelRef('');
                        },
                        'Model added.',
                      );
                    }}
                  >
                    Save model
                  </Button>
                </Box>
              </Stack>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      {/* --- routing ------------------------------------------------------- */}
      <Card>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            Primary and fallback order
          </Typography>
          <Stack spacing={2}>
            <TextField
              select
              label="Primary model"
              value={primaryId}
              disabled={!canWriteRouting || pending !== null}
              onChange={(event) => {
                void run(
                  'routing',
                  () => saveChain(event.target.value, fallbackIds),
                  'Primary model saved.',
                );
              }}
            >
              {models.map((model) => (
                <MenuItem key={model.model_id} value={model.model_id}>
                  {modelLabel(model.model_id)}
                </MenuItem>
              ))}
            </TextField>

            <Box component="ol" aria-label="Fallback order" sx={{ pl: 3, m: 0 }}>
              {fallbackIds.map((modelId, index) => (
                <li key={modelId}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{modelLabel(modelId)}</Typography>
                    <Button
                      size="small"
                      aria-label={`Move ${modelLabel(modelId)} up`}
                      disabled={!canWriteRouting || index === 0 || pending !== null}
                      onClick={() => {
                        move(index, -1);
                      }}
                    >
                      Move up
                    </Button>
                    <Button
                      size="small"
                      aria-label={`Move ${modelLabel(modelId)} down`}
                      disabled={
                        !canWriteRouting || index === fallbackIds.length - 1 || pending !== null
                      }
                      onClick={() => {
                        move(index, 1);
                      }}
                    >
                      Move down
                    </Button>
                  </Stack>
                </li>
              ))}
            </Box>

            {canWriteRouting ? (
              <Box>
                <TextField
                  select
                  label="Add a fallback model"
                  value=""
                  disabled={pending !== null}
                  sx={{ minWidth: 280 }}
                  onChange={(event) => {
                    void run(
                      'routing',
                      () => saveChain(primaryId, [...fallbackIds, event.target.value]),
                      'Fallback added.',
                    );
                  }}
                >
                  {models
                    .filter(
                      (model) =>
                        model.model_id !== primaryId && !fallbackIds.includes(model.model_id),
                    )
                    .map((model) => (
                      <MenuItem key={model.model_id} value={model.model_id}>
                        {modelLabel(model.model_id)}
                      </MenuItem>
                    ))}
                </TextField>
              </Box>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
